import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useRef } from 'react';

import { requestsKeys } from './useRequestsQuery';
import { networkService } from '../../services/network';
import { offlineCache } from '../../services/offlineCache';
import { offlineRequestsService } from '../../services/offlineRequestsService';
import { syncService } from '../../services/syncService';
import { CreateRequestData, Request, UpdateRequestData } from '../../types';
import { shouldMarkDetailCacheNeedsRejectionHydration } from '../../utils/requestDetailApprovalPoll';

/** Cache-only: list pre-population omits detail GET fields (e.g. rejection read-model). */
export type OfflineRequestDetailCache = Request & {
  _offlineDetailNeedsHydration?: boolean;
};

export function shouldRefetchOfflineDetailOnMount(
  connected: boolean,
  data: OfflineRequestDetailCache | undefined,
): boolean | 'always' {
  if (!connected) {
    return false;
  }
  return data?._offlineDetailNeedsHydration === true ? 'always' : false;
}

// Query keys for consistent cache management
export const offlineRequestsKeys = {
  all: ['offline-requests'] as const,
  lists: () => [...offlineRequestsKeys.all, 'list'] as const,
  details: () => [...offlineRequestsKeys.all, 'detail'] as const,
  detail: (id: string) => [...offlineRequestsKeys.details(), id] as const,
  stats: () => [...offlineRequestsKeys.all, 'stats'] as const,
};

/**
 * Hook for fetching all requests with offline support
 */
export const useOfflineRequestsQuery = () => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: offlineRequestsKeys.lists(),
    queryFn: async () => {
      const requests = await offlineRequestsService.getAllRequests();

      // Pre-populate individual request caches for offline support
      requests.forEach(request => {
        if (request.id && !request.id.startsWith('o-')) {
          queryClient.setQueryData(
            offlineRequestsKeys.detail(request.id),
            (oldData: any) => {
              if (!oldData) return request;
              // Never let a list row erase a creator boundary the detail read resolved.
              // List rows carry it too, but an offline or degraded list may not.
              // Same for latestRejectionReason: list API omits it; detail GET owns it.
              const hydratedFromDetail =
                oldData?._offlineDetailNeedsHydration === false;
              const rejectionHydrationNeeded =
                shouldMarkDetailCacheNeedsRejectionHydration(
                  request.sysRequestStage ?? request.stage,
                  oldData?.latestRejectionReason ??
                    request.latestRejectionReason,
                );
              return {
                ...request,
                requestorIsExternal:
                  request.requestorIsExternal ?? oldData.requestorIsExternal,
                latestRejectionReason:
                  oldData.latestRejectionReason ??
                  request.latestRejectionReason,
                latestRejectionRejectedByEmail:
                  oldData.latestRejectionRejectedByEmail ??
                  request.latestRejectionRejectedByEmail,
                _offlineDetailNeedsHydration: rejectionHydrationNeeded
                  ? true
                  : hydratedFromDetail
                  ? false
                  : true,
              };
            },
          );
        }
      });

      return requests;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - reasonable refresh interval
    gcTime: 10 * 60 * 1000, // 10 minutes cache time
    retry: false, // Don't retry on failure - let the offline service handle it
    refetchOnWindowFocus: false, // Prevent refetch when coming back to tab offline
    refetchOnReconnect: true, // Refetch when network comes back
    refetchOnMount: true, // Refetch on mount to get fresh data, but show cached data while fetching
    placeholderData: previousData => previousData, // Keep previous data visible during refetch
    networkMode: 'always', // Allow queries to run even when offline
    meta: {
      errorMessage: 'Failed to fetch requests. Showing cached data.',
    },
  });
};

/**
 * Hook for fetching a single request by ID with offline support
 */
export const useOfflineRequestQuery = (id: string) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: offlineRequestsKeys.detail(id),
    queryFn: async () => {
      // When offline, first check if this request exists in the list cache
      // This prevents "request not found" errors when the detail cache was garbage collected
      // but the request is still in the list
      if (!networkService.isConnected() && !id.startsWith('o-')) {
        const listCache = queryClient.getQueryData<any[]>(
          offlineRequestsKeys.lists(),
        );

        if (listCache) {
          const requestFromList = listCache.find((req: any) => req.id === id);
          if (requestFromList) {
            return requestFromList;
          }
        }
      }

      const request = await offlineRequestsService.getRequestById(id);

      if (!request && id.startsWith('o-')) {
        console.error(
          `Offline request ${id} not found in cache - this should not happen after creation`,
        );
      }

      // If offline and request is still null, check list cache one more time as a safety net
      if (!request && !networkService.isConnected()) {
        const listCache = queryClient.getQueryData<any[]>(
          offlineRequestsKeys.lists(),
        );
        if (listCache) {
          const requestFromList = listCache.find((req: any) => req.id === id);
          if (requestFromList) {
            return requestFromList;
          }
        }
      }

      if (!request) {
        return request;
      }
      return {
        ...request,
        _offlineDetailNeedsHydration: false,
      };
    },
    enabled: !!id,
    networkMode: 'always', // Allow query to run even when offline - critical for offline requests!
    staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh for this duration
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours to support offline usage
    retry: (failureCount, _error) => {
      if (!networkService.isConnected()) return false;
      return failureCount < 2;
    },
    refetchOnWindowFocus: false, // Prevent refetch when coming back to tab offline
    refetchOnReconnect: false, // CRITICAL: Disable refetch on reconnect to prevent stale data overwrites
    refetchInterval: false, // CRITICAL: Disable background refetch to prevent stale data overwrites
    // Refetch only when list pre-population flagged the cache; avoids GET-on-mount races
    // with in-flight optimistic edits after a prior detail hydration.
    refetchOnMount: query =>
      shouldRefetchOfflineDetailOnMount(
        networkService.isConnected(),
        query.state.data as OfflineRequestDetailCache | undefined,
      ),
    // REMOVED: placeholderData - it locks the observer to placeholder data until network refetch completes
    // This prevents optimistic cache updates from triggering re-renders on slow connections
    notifyOnChangeProps: 'all', // Force re-render on ANY cache change to ensure optimistic updates are visible
    meta: {
      errorMessage: 'Failed to fetch request details.',
    },
  });

  return query;
};

/**
 * Hook for creating a new request with simplified offline-first approach
 */
export const useOfflineCreateRequest = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: CreateRequestData) => {
      // Use the service to create the request (handles online/offline automatically)
      const result = await offlineRequestsService.createRequest(data);
      return result;
    },
    onSuccess: createdRequest => {
      // If it's a real request (not offline placeholder), update the cache
      if (
        createdRequest &&
        'id' in createdRequest &&
        !createdRequest.id.startsWith('o-')
      ) {
        // Add the new request to the beginning of the cached list
        queryClient.setQueryData(
          offlineRequestsKeys.lists(),
          (oldData: any) => {
            if (!oldData) return [createdRequest];
            return [createdRequest, ...oldData];
          },
        );

        // Also cache the individual request
        queryClient.setQueryData(
          offlineRequestsKeys.detail(createdRequest.id),
          createdRequest,
        );
      } else {
        // For offline requests, invalidate to trigger a refetch that will include the offline request
        queryClient.invalidateQueries({
          queryKey: offlineRequestsKeys.lists(),
        });
      }
    },
    onError: error => {
      console.error('Failed to create request:', error);
    },
  });

  return mutation;
};

/**
 * Hook for updating a request (works offline and online)
 */
export const useOfflineUpdateRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRequestData }) =>
      offlineRequestsService.updateRequest(id, data),
    networkMode: 'always', // Allow mutations to run even when offline
    onSuccess: updatedRequest => {
      if (updatedRequest) {
        // Merge with existing cached data to preserve team members and other fields
        queryClient.setQueryData(
          offlineRequestsKeys.detail(updatedRequest.id),
          (oldData: any) => {
            if (!oldData) return updatedRequest;
            // Preserve team member arrays from old data if they're missing in the update
            return {
              ...updatedRequest,
              // The save response is not stamped with the creator boundary (only the list
              // and the single-request read are), so keep whichever answer we already had.
              // Dropping it would flip the Draft submit label mid-session on an internal
              // creator's request without anything on the server having changed.
              requestorIsExternal:
                updatedRequest.requestorIsExternal ??
                oldData.requestorIsExternal,
              vertivSponsors:
                updatedRequest.vertivSponsors ?? oldData.vertivSponsors ?? [],
              vertivContributors:
                updatedRequest.vertivContributors ??
                oldData.vertivContributors ??
                [],
              partnerContributors:
                updatedRequest.partnerContributors ??
                oldData.partnerContributors ??
                [],
            };
          },
        );

        // Update the request in the list cache
        queryClient.setQueryData(
          offlineRequestsKeys.lists(),
          (oldData: any) => {
            if (!oldData) return oldData;
            return oldData.map((req: any) => {
              if (req.id === updatedRequest.id) {
                // Preserve team members from existing list data
                return {
                  ...updatedRequest,
                  vertivSponsors:
                    updatedRequest.vertivSponsors ?? req.vertivSponsors ?? [],
                  vertivContributors:
                    updatedRequest.vertivContributors ??
                    req.vertivContributors ??
                    [],
                  partnerContributors:
                    updatedRequest.partnerContributors ??
                    req.partnerContributors ??
                    [],
                };
              }
              return req;
            });
          },
        );

        // Invalidate paginated queries to reflect changes in ViewRequests table immediately
        // This ensures the request list shows updated data (like modifiedBy) without manual refresh
        queryClient.invalidateQueries({
          queryKey: requestsKeys.all,
          refetchType: 'active', // Only refetch queries that are currently being used
        });
      }
    },
    meta: {
      errorMessage: 'Failed to update request.',
    },
  });
};

/**
 * Hook for deleting a request (online only)
 */
export const useOfflineDeleteRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: offlineRequestsService.deleteRequest,
    onSuccess: async (_, deletedId) => {
      // Optimistically remove the request from the list cache immediately
      queryClient.setQueryData(offlineRequestsKeys.lists(), (oldData: any) => {
        if (!oldData) return oldData;
        return oldData.filter((request: any) => request.id !== deletedId);
      });

      // Remove the request detail from cache
      queryClient.removeQueries({
        queryKey: offlineRequestsKeys.detail(deletedId),
      });
      queryClient.removeQueries({ queryKey: requestsKeys.detail(deletedId) });

      // Update offline cache to reflect deletion
      try {
        const { requests } = await offlineCache.getCachedRequests();
        const updatedRequests = requests.filter(req => req.id !== deletedId);
        await offlineCache.cacheRequests(updatedRequests);
      } catch (error) {
        console.error('Failed to update offline cache after deletion:', error);
      }

      // Invalidate all request queries including paginated ones to reflect changes in ViewRequests table
      queryClient.invalidateQueries({
        queryKey: offlineRequestsKeys.lists(),
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: requestsKeys.all,
        refetchType: 'active',
      });
    },
    meta: {
      errorMessage:
        'Failed to archive request. Archiving requires internet connection.',
    },
  });
};

/**
 * Hook for offline statistics
 */
export const useOfflineStatsQuery = () => {
  return useQuery({
    queryKey: offlineRequestsKeys.stats(),
    queryFn: offlineRequestsService.getOfflineStats,
    refetchInterval: false, // Disabled - was causing request loops
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook for manual sync operations
 */
export const useSyncMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: syncService.fullSync,
    onSuccess: async () => {
      // Invalidate queries to trigger background refresh
      // The sync service already updated the cache with synced requests

      queryClient.invalidateQueries({
        queryKey: offlineRequestsKeys.all,
        refetchType: 'active', // Only refetch active queries
      });

      // Also invalidate paginated queries to ensure ViewRequests shows updated data
      queryClient.invalidateQueries({
        queryKey: ['requests'],
        refetchType: 'active',
      });

      // CRITICAL: Invalidate sync status to update the header indicator
      queryClient.invalidateQueries({
        queryKey: ['sync-status'],
        refetchType: 'active',
      });
    },
    onError: error => {
      console.error('useSyncMutation: Sync failed:', error);

      // Also invalidate sync status on error to clear "syncing" indicator
      queryClient.invalidateQueries({
        queryKey: ['sync-status'],
      });
    },
    meta: {
      errorMessage: 'Failed to sync offline data.',
    },
  });
};

/**
 * Hook for network status with React Query integration
 */
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(networkService.isConnected());
  const queryClient = useQueryClient();
  const lastSyncTimeRef = useRef<number>(0);

  useEffect(() => {
    const cleanup = networkService.addListener(async connected => {
      setIsOnline(connected);

      if (connected) {
        // Prevent duplicate syncs within 5 seconds (e.g., app initialization already synced)
        const now = Date.now();
        const timeSinceLastSync = now - lastSyncTimeRef.current;

        if (timeSinceLastSync < 5000) {
          return;
        }
        lastSyncTimeRef.current = now;

        try {
          // Run sync and get result
          await syncService.fullSync();

          // OPTIMIZED FIX: Use invalidateQueries for non-blocking updates
          // The sync service already updated the cache with synced requests
          // invalidateQueries marks queries as stale and triggers background refetch
          // This is MUCH faster than waiting for refetchQueries to complete
          // The placeholderData in the queries will keep showing cached data during the refetch
          queryClient.invalidateQueries({
            queryKey: offlineRequestsKeys.all,
            refetchType: 'active', // Only refetch active queries
          });

          queryClient.invalidateQueries({
            queryKey: ['requests'],
            refetchType: 'active',
          });

          queryClient.invalidateQueries({
            queryKey: ['sync-status'],
            refetchType: 'active',
          });
        } catch (error) {
          console.error('❌ Auto-sync failed:', error);

          // Still invalidate even if sync fails to ensure UI refreshes
          queryClient.invalidateQueries({
            queryKey: offlineRequestsKeys.all,
            refetchType: 'active',
          });

          queryClient.invalidateQueries({
            queryKey: ['sync-status'],
            refetchType: 'active',
          });
        }
      }
    });

    return cleanup;
  }, [queryClient]);

  return isOnline;
};

/**
 * Hook for getting offline time
 */
export const useOfflineTime = () => {
  const [offlineTime, setOfflineTime] = useState(
    networkService.getOfflineSinceTime(),
  );

  useEffect(() => {
    const cleanup = networkService.addListener(connected => {
      if (!connected) {
        setOfflineTime(networkService.getOfflineSinceTime());
      } else {
        setOfflineTime(null);
      }
    });

    return cleanup;
  }, []);

  return offlineTime;
};

/**
 * Hook for sync status monitoring
 */
export const useSyncStatusQuery = () => {
  return useQuery({
    queryKey: ['sync-status'],
    queryFn: syncService.getSyncStatus,
    refetchInterval: false, // Disabled - was causing request loops
    staleTime: 500, // 500ms - very fresh to ensure sync button updates quickly
    gcTime: 1000 * 60 * 5, // 5 minutes cache time
    refetchOnMount: true, // Always check on mount
    refetchOnWindowFocus: true, // Refetch when returning to app/tab
    refetchOnReconnect: true, // Refetch when network restored
  });
};

/**
 * Hook for retrying failed offline requests
 */
export const useRetryFailedMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: syncService.retryFailedRequests,
    onSuccess: async () => {
      // Invalidate queries for non-blocking updates
      queryClient.invalidateQueries({
        queryKey: offlineRequestsKeys.all,
        refetchType: 'active',
      });

      queryClient.invalidateQueries({
        queryKey: ['requests'],
        refetchType: 'active',
      });

      queryClient.invalidateQueries({
        queryKey: ['sync-status'],
        refetchType: 'active',
      });
    },
    onError: error => {
      console.error('useRetryFailedMutation: Retry failed:', error);

      // Also invalidate sync status on error to clear "syncing" indicator
      queryClient.invalidateQueries({
        queryKey: ['sync-status'],
      });
    },
    meta: {
      errorMessage: 'Failed to retry offline requests.',
    },
  });
};
